import * as yup from "yup";
import { createAddressFields } from "../constants";
import { type FormSectionConfig, type SelectOption, type TaxFormConfig } from "../types";

export const CRS_FATCA_STATUS_OPTIONS: SelectOption[] = [
  { value: "ffi_reporting_model_1_ffi", label: "FFI – Reporting Model 1 FFI" },
  { value: "ffi_reporting_model_2_ffi", label: "FFI – Reporting Model 2 FFI" },
  {
    value: "ffi_participating_ffi",
    label: "FFI – Participating Foreign Financial Institution",
  },
  {
    value: "ffi_registered_deemed_compliant_ffi",
    label: "FFI – Registered Deemed-Compliant Foreign Financial Institution",
  },
  { value: "ffi_sponsored_ffi", label: "FFI – Sponsored Foreign Financial Institution" },
  { value: "ffi_trustee_documented_trust", label: "FFI – Trustee-Documented Trust" },
  {
    value: "ffi_certified_deemed_compliant_ffi",
    label: "FFI – Certified Deemed-Compliant / Non-Reporting Foreign Financial Institution",
  },
  {
    value: "ffi_non_participating_ffi",
    label: "FFI – Non-Participating Foreign Financial Institution",
  },
  { value: "nffe_exempt_beneficial_owner", label: "NFFE – Exempt Beneficial Owner" },
  { value: "nffe_active_nffe", label: "NFFE – Active Non-Financial Foreign Entity" },
  { value: "nffe_passive_nffe", label: "NFFE – Passive Non-Financial Foreign Entity" },
  { value: "nffe_direct_reporting_nffe", label: "NFFE – Direct Reporting NFFE" },
  {
    value: "nffe_sponsored_direct_reporting_nffe",
    label: "NFFE – Sponsored Direct Reporting NFFE",
  },
  { value: "odffi_owner_documented_ffi", label: "ODFFI – Owner-Documented FFI" },
  { value: "no_fatca_status", label: "No FATCA status on form (OECD version)" },
];

export const CRS_STATUS_OPTIONS: SelectOption[] = [
  { value: "participating_ffi", label: "Participating FFI" },
  { value: "non_participating_ffi", label: "Non Participating FFI" },
  { value: "active_nffe", label: "Active NFFE" },
  { value: "passive_nffe", label: "Passive NFFE" },
  { value: "reporting_ffi", label: "Reporting FFI" },
  { value: "non_reporting_ffi", label: "Non Reporting FFI" },
  { value: "eu_reportable", label: "EU Reportable" },
];

export const CRS_CONTROLLING_PERSON_TYPE_OPTIONS: SelectOption[] = [
  { label: "LP-Control by ownership", value: "ownership" },
  { label: "LP-Control by other means", value: "other_means" },
  { label: "LP-Senior Managing Official", value: "senior_managing_official" },
  { label: "Trust-Settlor", value: "trust_settlor" },
  { label: "Trust-Trustee", value: "trust_trustee" },
  { label: "Trust-Protector", value: "trust_protector" },
  { label: "Trust-Beneficiary", value: "trust_beneficiary" },
  { label: "Trust-Other", value: "trust_other" },
  { label: "Other-Settlor Equivalent", value: "other_settlor" },
  { label: "Other-Trustee Equivalent", value: "other_trustee" },
  { label: "Other-Protector Equivalent", value: "other_protector" },
  { label: "Other-Beneficiary Equivalent", value: "other_beneficiary" },
  { label: "Other-Other Equivalent", value: "other_other" },
];

const sections: FormSectionConfig[] = [
  {
    id: "accountHolderIdentification",
    title: "Account Holder Identification",
    fields: [
      {
        name: "entityName",
        type: "text",
        label: "Entity name",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfIncorporation",
        type: "country",
        label: "Country of incorporation",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "_permanentAddressLabel",
        type: "label",
        label: "Permanent residence address",
        required: true,
        linkedField: "permanentAddress",
        gridColumn: "full",
      },
      ...createAddressFields("permanentAddress"),
    ],
  },
  {
    id: "usFatcaClassification",
    title: "US FATCA Classification for All Non United States Entities",
    fields: [
      {
        name: "fatcaStatus",
        type: "select",
        label: "FATCA status",
        placeholder: "Select a status",
        required: true,
        gridColumn: "half",
        options: CRS_FATCA_STATUS_OPTIONS,
        tooltip: {
          content:
            'If OECD version of Self Certification Form is submitted FATCA status will not be provided. Please select "No FATCA Status on form" and obtain FATCA status from a W-Series form.',
        },
      },
      {
        name: "crsStatus",
        type: "select",
        label: "CRS status",
        placeholder: "Select a status",
        required: true,
        gridColumn: "half",
        options: CRS_STATUS_OPTIONS,
      },
      {
        name: "giin",
        type: "giin",
        label: "GIIN",
        placeholder: "XXXXXX.XXXXX.XX.XXX",
        tooltip: {
          content:
            "GIIN is a 19-character identifier (including dots) and cannot contain the letter 'O'.",
        },
        gridColumn: "full",
      },
      {
        name: "sponsorName",
        type: "text",
        label: "Sponsor name",
        placeholder: "",
        gridColumn: "half",
      },
      {
        name: "sponsorGiin",
        type: "giin",
        label: "Sponsor GIIN",
        placeholder: "XXXXXX.XXXXX.XX.XXX",
        tooltip: {
          content:
            "GIIN is a 19-character identifier (including dots) and cannot contain the letter 'O'.",
        },
        gridColumn: "half",
      },
      {
        name: "giinVerified",
        type: "checkbox",
        label: "Check if GIIN and Sponsor GIIN has been verified through the IRS portal",
        gridColumn: "full",
      },
    ],
  },
  {
    id: "taxResidency",
    title: "TAX RESIDENCY(IES)",
    actionLabel: "Add tax residency",
    fields: [
      {
        name: "taxResidencies",
        type: "taxResidency",
        label: "TAX RESIDENCY(IES)",
        required: true,
        gridColumn: "full",
      },
    ],
  },
  {
    id: "crsControllingPersons",
    title: "CRS CONTROLLING PERSONS",
    actionLabel: "Add controlling person",
    tooltip: {
      content:
        "The CRS Controlling Persons section identifies individuals defined within an Entity Self-Certification form as controlling persons.",
    },
    fields: [
      {
        name: "crsControllingPersons",
        type: "controllingPersons",
        label: "CRS CONTROLLING PERSONS",
        gridColumn: "full",
        emptyStateText: "There are no controlling persons.",
        infoText:
          "Ensure the information below matches the CRS tax document. Any information saved here is specific to this CRS controlling person.",
        addButtonText: "Add controlling person",
        updateButtonText: "Update controlling person",
        searchLabel: "Search existing profile or add new controlling person",
        showDateOfBirth: false,
        controllingPersonTypeLabel: "CRS controlling person type",
        searchInputPlaceholder: "Enter controlling person name",
        searchHelperText:
          "Using an existing profile only pre-fills the controlling person information and does not link to the profile used. If the controlling person is not listed, enter details manually.",
        controllingPersonTypeOptions: CRS_CONTROLLING_PERSON_TYPE_OPTIONS,
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  entityName: yup.string().required("Entity name is required"),
  countryOfIncorporation: yup.string().required("Country of incorporation is required"),
  permanentAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  fatcaStatus: yup.string().required("FATCA status is required"),
  crsStatus: yup.string().required("CRS status is required"),
  taxResidencies: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        jurisdiction: yup.string().required("Jurisdiction of tax residency is required"),
        hasTaxReferenceNumber: yup.boolean().required(),
        taxReferenceNumberType: yup.string().when("hasTaxReferenceNumber", {
          is: true,
          then: (schema) => schema.required("Tax reference number type is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        taxReferenceNumber: yup.string().when("hasTaxReferenceNumber", {
          is: true,
          then: (schema) => schema.required("Tax reference number is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
        reasonForNoTin: yup.string().when("hasTaxReferenceNumber", {
          is: false,
          then: (schema) => schema.required("Reason for no TIN is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    )
    .min(1, "Tax residency is required"),
  giinVerified: yup.boolean().when(["giin", "sponsorGiin"], {
    is: (giin: string, sponsorGiin: string) => Boolean(giin?.trim() || sponsorGiin?.trim()),
    then: (schema) =>
      schema.test("giin-validation", "", function (value) {
        const { giin, sponsorGiin } = this.parent as { giin: string; sponsorGiin: string };
        const isGiinIncomplete = giin?.trim() && giin.replace(/\./g, "").length !== 16;
        const isSponsorIncomplete =
          sponsorGiin?.trim() && sponsorGiin.replace(/\./g, "").length !== 16;
        if (isGiinIncomplete || isSponsorIncomplete) {
          return this.createError({ message: "GIIN provided is incomplete" });
        }
        if (value !== true) {
          return this.createError({
            message: "GIIN provided must be verified through the IRS portal",
          });
        }
        return true;
      }),
  }),
});

const initialValues = {
  entityName: "",
  countryOfIncorporation: "",
  permanentAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  fatcaStatus: "",
  crsStatus: "",
  giin: "",
  sponsorName: "",
  sponsorGiin: "",
  giinVerified: false,
  taxResidencies: [],
  crsControllingPersons: [],
};

export const crsEntityConfig: TaxFormConfig = {
  formId: "CRS_ENTITY",
  title: "New CRS details",
  subtitle: "Entity Self Certification",
  sections,
  validationSchema,
  initialValues,
};
